import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Zap, 
  Wallet,
  CreditCard,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  ArrowUpRight,
  ArrowDownLeft,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  LogOut,
  Sparkles,
  Battery,
  Euro,
  UserCheck,
  Percent,
  ShieldCheck,
  AlertCircle,
  Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WalletPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [account, setAccount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [energyTransactions, setEnergyTransactions] = useState([]);
  const [entryFee, setEntryFee] = useState(9.90);
  const [commissionRate, setCommissionRate] = useState(0.10);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/account`, { headers });
      
      setAccount(response.data.account);
      setPayments(response.data.payments || []);
      setEnergyTransactions(response.data.energy_transactions || []);
      setEntryFee(response.data.entry_fee || 9.90);
      setCommissionRate(response.data.commission_rate || 0.10);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPolling(false);
      toast.error('Vérification du paiement expirée');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${API_URL}/api/payments/status/${sessionId}`,
        { headers }
      );

      if (response.data.payment_status === 'paid') {
        setPolling(false);
        toast.success('Paiement réussi ! Accès à la plateforme activé.');
        fetchData();
        navigate('/wallet', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        setPolling(false);
        toast.error('Session de paiement expirée');
        navigate('/wallet', { replace: true });
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Poll error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [fetchData, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      
      const sessionId = searchParams.get('session_id');
      const status = searchParams.get('status');
      
      if (sessionId && status === 'success') {
        setPolling(true);
        toast.info('Vérification du paiement en cours...');
        pollPaymentStatus(sessionId);
      } else if (status === 'cancelled') {
        toast.info('Paiement annulé');
        navigate('/wallet', { replace: true });
      }
    }
  }, [isAuthenticated, searchParams, fetchData, pollPaymentStatus, navigate]);

  const handlePayEntryFee = async () => {
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_URL}/api/payments/entry-fee/checkout`,
        { origin_url: originUrl },
        { headers }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const isConsumer = user?.user_type === 'consumer';
  const isProducer = user?.user_type === 'producer';
  const entryFeePaid = account?.entry_fee_paid;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/wallet" className="nav-link active">Mon Compte</Link>
              <Link to="/sepa" className="nav-link">SEPA</Link>
              <Link to="/linky" className="nav-link">Linky</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {isProducer ? 'Producteur' : 'Consommateur'}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Mon Compte
                </h1>
                <p className="text-muted-foreground">
                  {isProducer ? 'Gérez vos revenus et commissions' : 'Gérez votre accès et vos achats'}
                </p>
              </div>
            </div>
          </div>

          {/* Polling indicator */}
          {polling && (
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-blue-700">Vérification du paiement en cours...</span>
              </CardContent>
            </Card>
          )}

          {/* Entry Fee Card (Consumer only) */}
          {isConsumer && (
            <Card className={`mb-6 ${entryFeePaid ? 'border-green-200 bg-green-50/50' : 'border-orange-200 bg-orange-50'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {entryFeePaid ? (
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg">
                        {entryFeePaid ? 'Accès activé' : 'Droit d\'entrée requis'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {entryFeePaid 
                          ? `Payé le ${new Date(account?.entry_fee_paid_at).toLocaleDateString('fr-FR')}`
                          : 'Pour accéder aux achats d\'énergie sur la plateforme'
                        }
                      </p>
                    </div>
                  </div>
                  
                  {!entryFeePaid && (
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold">{entryFee}€</p>
                        <p className="text-xs text-muted-foreground">Paiement unique</p>
                      </div>
                      <Button 
                        onClick={handlePayEntryFee}
                        disabled={processing}
                        className="btn-primary"
                      >
                        {processing ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Payer
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {entryFeePaid && (
                    <Badge className="bg-green-100 text-green-700 py-1.5 px-3">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Accès complet
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Balance Card (Producer) */}
            {isProducer && (
              <Card className="md:col-span-2 bg-gradient-to-br from-primary to-emerald-600 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <CardContent className="p-8 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white/70 text-sm mb-1">Solde disponible</p>
                      <p className="text-5xl font-bold mb-2">
                        {account?.balance_eur?.toFixed(2) || '0.00'} <span className="text-2xl">€</span>
                      </p>
                      <p className="text-white/70 text-sm">
                        À recevoir lors du prochain versement
                      </p>
                    </div>
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                      <Euro className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/20">
                    <div>
                      <p className="text-white/70 text-xs">Total gagné</p>
                      <p className="text-xl font-semibold">
                        {account?.total_earned_eur?.toFixed(2) || '0.00'}€
                      </p>
                    </div>
                    <div>
                      <p className="text-white/70 text-xs">Énergie vendue</p>
                      <p className="text-xl font-semibold">
                        {account?.energy_sold_kwh?.toFixed(1) || '0.0'} kWh
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Consumer Stats */}
            {isConsumer && (
              <Card className="md:col-span-2">
                <CardContent className="p-6">
                  <h3 className="font-bold mb-4">Vos achats d'énergie</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-primary">
                        {account?.energy_bought_kwh?.toFixed(1) || '0.0'}
                      </p>
                      <p className="text-sm text-muted-foreground">kWh achetés</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-primary">
                        {account?.total_spent_eur?.toFixed(2) || '0.00'}€
                      </p>
                      <p className="text-sm text-muted-foreground">Total dépensé</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Commission Info (Producer) */}
            {isProducer && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Percent className="w-5 h-5 text-primary" />
                    <h3 className="font-bold">Commission Green Teva</h3>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-2">
                    {(commissionRate * 100).toFixed(0)}%
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Prélevé sur chaque vente d'énergie
                  </p>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      Total commissions payées
                    </p>
                    <p className="font-semibold">
                      {account?.total_commission_paid_eur?.toFixed(2) || '0.00'}€
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SEPA Link Card */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="font-bold">Prélèvement SEPA</h3>
                      <p className="text-sm text-muted-foreground">
                        {isProducer ? 'Recevez vos revenus automatiquement' : 'Payez automatiquement chaque mois'}
                      </p>
                    </div>
                  </div>
                  <Button asChild variant="outline" className="border-blue-200">
                    <Link to="/sepa">
                      Configurer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className={isProducer ? '' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Activité récente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {energyTransactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucune transaction d'énergie
                  </p>
                ) : (
                  energyTransactions.slice(0, 5).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {tx.producer_id === user?.id ? (
                          <ArrowDownLeft className="w-4 h-4 text-green-500" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-red-500" />
                        )}
                        <span>{tx.energy_kwh} kWh</span>
                      </div>
                      <span className={`font-medium ${
                        tx.producer_id === user?.id ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.producer_id === user?.id 
                          ? `+${tx.producer_amount?.toFixed(2)}€`
                          : `-${tx.total_amount?.toFixed(2)}€`
                        }
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Historique des paiements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun paiement effectué</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {payment.payment_type === 'entry_fee' 
                              ? 'Droit d\'entrée' 
                              : 'Paiement'
                            }
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{payment.amount}€</p>
                        <Badge className="bg-green-100 text-green-700">
                          Payé
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default WalletPage;
